import * as React from "react";
import type { CSSProperties, HTMLAttributes } from "react";
import type { AbstractNode, IconDefinition } from "@ant-design/icons-svg/lib/types";
import BellOutlinedDefinition from "@ant-design/icons-svg/lib/asn/BellOutlined";
import AuditOutlinedDefinition from "@ant-design/icons-svg/lib/asn/AuditOutlined";
import BookOutlinedDefinition from "@ant-design/icons-svg/lib/asn/BookOutlined";
import CameraOutlinedDefinition from "@ant-design/icons-svg/lib/asn/CameraOutlined";
import CloudDownloadOutlinedDefinition from "@ant-design/icons-svg/lib/asn/CloudDownloadOutlined";
import CloudServerOutlinedDefinition from "@ant-design/icons-svg/lib/asn/CloudServerOutlined";
import CheckCircleOutlinedDefinition from "@ant-design/icons-svg/lib/asn/CheckCircleOutlined";
import CheckOutlinedDefinition from "@ant-design/icons-svg/lib/asn/CheckOutlined";
import CloseCircleOutlinedDefinition from "@ant-design/icons-svg/lib/asn/CloseCircleOutlined";
import DashboardOutlinedDefinition from "@ant-design/icons-svg/lib/asn/DashboardOutlined";
import DeleteOutlinedDefinition from "@ant-design/icons-svg/lib/asn/DeleteOutlined";
import EditOutlinedDefinition from "@ant-design/icons-svg/lib/asn/EditOutlined";
import FileTextOutlinedDefinition from "@ant-design/icons-svg/lib/asn/FileTextOutlined";
import IdcardOutlinedDefinition from "@ant-design/icons-svg/lib/asn/IdcardOutlined";
import KeyOutlinedDefinition from "@ant-design/icons-svg/lib/asn/KeyOutlined";
import LockOutlinedDefinition from "@ant-design/icons-svg/lib/asn/LockOutlined";
import LogoutOutlinedDefinition from "@ant-design/icons-svg/lib/asn/LogoutOutlined";
import MenuFoldOutlinedDefinition from "@ant-design/icons-svg/lib/asn/MenuFoldOutlined";
import MenuUnfoldOutlinedDefinition from "@ant-design/icons-svg/lib/asn/MenuUnfoldOutlined";
import NotificationOutlinedDefinition from "@ant-design/icons-svg/lib/asn/NotificationOutlined";
import PlusOutlinedDefinition from "@ant-design/icons-svg/lib/asn/PlusOutlined";
import ReloadOutlinedDefinition from "@ant-design/icons-svg/lib/asn/ReloadOutlined";
import RocketOutlinedDefinition from "@ant-design/icons-svg/lib/asn/RocketOutlined";
import SafetyCertificateOutlinedDefinition from "@ant-design/icons-svg/lib/asn/SafetyCertificateOutlined";
import SafetyOutlinedDefinition from "@ant-design/icons-svg/lib/asn/SafetyOutlined";
import SaveOutlinedDefinition from "@ant-design/icons-svg/lib/asn/SaveOutlined";
import SendOutlinedDefinition from "@ant-design/icons-svg/lib/asn/SendOutlined";
import SettingOutlinedDefinition from "@ant-design/icons-svg/lib/asn/SettingOutlined";
import StopOutlinedDefinition from "@ant-design/icons-svg/lib/asn/StopOutlined";
import SyncOutlinedDefinition from "@ant-design/icons-svg/lib/asn/SyncOutlined";
import TeamOutlinedDefinition from "@ant-design/icons-svg/lib/asn/TeamOutlined";
import ThunderboltOutlinedDefinition from "@ant-design/icons-svg/lib/asn/ThunderboltOutlined";
import UserOutlinedDefinition from "@ant-design/icons-svg/lib/asn/UserOutlined";
import UserSwitchOutlinedDefinition from "@ant-design/icons-svg/lib/asn/UserSwitchOutlined";
import UnlockOutlinedDefinition from "@ant-design/icons-svg/lib/asn/UnlockOutlined";
import UploadOutlinedDefinition from "@ant-design/icons-svg/lib/asn/UploadOutlined";
import InboxOutlinedDefinition from "@ant-design/icons-svg/lib/asn/InboxOutlined";
import LinkOutlinedDefinition from "@ant-design/icons-svg/lib/asn/LinkOutlined";
import BankOutlinedDefinition from "@ant-design/icons-svg/lib/asn/BankOutlined";
import ClusterOutlinedDefinition from "@ant-design/icons-svg/lib/asn/ClusterOutlined";
import ApartmentOutlinedDefinition from "@ant-design/icons-svg/lib/asn/ApartmentOutlined";
import CommentOutlinedDefinition from "@ant-design/icons-svg/lib/asn/CommentOutlined";

export type AppIconProps = HTMLAttributes<HTMLSpanElement> & {
  rotate?: number;
  spin?: boolean;
};

function isIconDefinition(value: unknown): value is IconDefinition {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<IconDefinition>;
  return (
    typeof candidate.name === "string" &&
    (typeof candidate.icon === "function" ||
      (typeof candidate.icon === "object" && candidate.icon !== null))
  );
}

function resolveDefinition(value: unknown): IconDefinition {
  let candidate = value;

  for (let depth = 0; depth < 3; depth += 1) {
    if (isIconDefinition(candidate)) {
      return candidate;
    }

    if (candidate && typeof candidate === "object" && "default" in candidate) {
      candidate = (candidate as { default?: unknown }).default;
      continue;
    }

    break;
  }

  throw new Error("Invalid Ant Design icon definition");
}

function normalizeAttrName(name: string) {
  if (name === "class") return "className";
  if (name.startsWith("aria-") || name.startsWith("data-")) return name;
  return name.replace(/-([a-z])/g, (_, letter: string) => letter.toUpperCase());
}

function normalizeAttrs(attrs: AbstractNode["attrs"]) {
  return Object.fromEntries(
    Object.entries(attrs).map(([name, value]) => [normalizeAttrName(name), value])
  );
}

function renderNode(
  node: AbstractNode,
  key: string,
  svgStyle?: CSSProperties
): React.ReactElement {
  const props: Record<string, unknown> = {
    ...normalizeAttrs(node.attrs),
    key,
  };

  if (node.tag === "svg") {
    props["aria-hidden"] = "true";
    props["data-icon"] = props["data-icon"] || key.replace(/^svg-/, "");
    props.fill = props.fill || "currentColor";
    props.focusable = "false";
    props.height = "1em";
    props.width = "1em";
    if (svgStyle) {
      props.style = svgStyle;
    }
  }

  return React.createElement(
    node.tag,
    props,
    node.children?.map((child, index): React.ReactElement =>
      renderNode(child, `${key}-${index}`)
    )
  );
}

function createIcon(definitionInput: unknown) {
  const definition = resolveDefinition(definitionInput);

  const Icon = React.forwardRef<HTMLSpanElement, AppIconProps>(
    ({ className, rotate, spin, ...props }, ref) => {
      const node =
        typeof definition.icon === "function"
          ? definition.icon("#1677ff", "#e6f4ff")
          : definition.icon;
      const svgStyle = rotate
        ? { transform: `rotate(${rotate}deg)` }
        : undefined;
      const classes = [
        "anticon",
        `anticon-${definition.name}`,
        spin ? "anticon-spin" : "",
        className || "",
      ]
        .filter(Boolean)
        .join(" ");

      return (
        <span
          {...props}
          ref={ref}
          role="img"
          aria-label={definition.name}
          className={classes}
        >
          {renderNode(node, `svg-${definition.name}`, svgStyle)}
        </span>
      );
    }
  );

  Icon.displayName = `${definition.name}-${definition.theme}`;
  return Icon;
}

export const AuditOutlined = createIcon(AuditOutlinedDefinition);
export const BellOutlined = createIcon(BellOutlinedDefinition);
export const BookOutlined = createIcon(BookOutlinedDefinition);
export const CameraOutlined = createIcon(CameraOutlinedDefinition);
export const CloudDownloadOutlined = createIcon(CloudDownloadOutlinedDefinition);
export const CloudServerOutlined = createIcon(CloudServerOutlinedDefinition);
export const CheckCircleOutlined = createIcon(CheckCircleOutlinedDefinition);
export const CheckOutlined = createIcon(CheckOutlinedDefinition);
export const CloseCircleOutlined = createIcon(CloseCircleOutlinedDefinition);
export const DashboardOutlined = createIcon(DashboardOutlinedDefinition);
export const DeleteOutlined = createIcon(DeleteOutlinedDefinition);
export const EditOutlined = createIcon(EditOutlinedDefinition);
export const FileTextOutlined = createIcon(FileTextOutlinedDefinition);
export const IdcardOutlined = createIcon(IdcardOutlinedDefinition);
export const KeyOutlined = createIcon(KeyOutlinedDefinition);
export const LinkOutlined = createIcon(LinkOutlinedDefinition);
export const LockOutlined = createIcon(LockOutlinedDefinition);
export const LogoutOutlined = createIcon(LogoutOutlinedDefinition);
export const MenuFoldOutlined = createIcon(MenuFoldOutlinedDefinition);
export const MenuUnfoldOutlined = createIcon(MenuUnfoldOutlinedDefinition);
export const NotificationOutlined = createIcon(NotificationOutlinedDefinition);
export const PlusOutlined = createIcon(PlusOutlinedDefinition);
export const ReloadOutlined = createIcon(ReloadOutlinedDefinition);
export const RocketOutlined = createIcon(RocketOutlinedDefinition);
export const SafetyCertificateOutlined = createIcon(SafetyCertificateOutlinedDefinition);
export const SafetyOutlined = createIcon(SafetyOutlinedDefinition);
export const SaveOutlined = createIcon(SaveOutlinedDefinition);
export const SendOutlined = createIcon(SendOutlinedDefinition);
export const SettingOutlined = createIcon(SettingOutlinedDefinition);
export const StopOutlined = createIcon(StopOutlinedDefinition);
export const SyncOutlined = createIcon(SyncOutlinedDefinition);
export const TeamOutlined = createIcon(TeamOutlinedDefinition);
export const ThunderboltOutlined = createIcon(ThunderboltOutlinedDefinition);
export const UnlockOutlined = createIcon(UnlockOutlinedDefinition);
export const UserOutlined = createIcon(UserOutlinedDefinition);
export const UploadOutlined = createIcon(UploadOutlinedDefinition);
export const InboxOutlined = createIcon(InboxOutlinedDefinition);
export const UserSwitchOutlined = createIcon(UserSwitchOutlinedDefinition);
export const BankOutlined = createIcon(BankOutlinedDefinition);
export const ClusterOutlined = createIcon(ClusterOutlinedDefinition);
export const ApartmentOutlined = createIcon(ApartmentOutlinedDefinition);
export const CommentOutlined = createIcon(CommentOutlinedDefinition);
